import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
	DialogTitle,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	TextField,
	Box,
	FormLabel,
	IconButton,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { getNewSkuItem, nameToSkuCode } from '../utils';
import { SkuItem } from 'common/modules/inventory';
import { uniq } from 'lodash';
import { api } from 'common/frontend/api';
import Spinner from 'common/components/Spinner';
import { RiInformationLine } from 'react-icons/ri';
import CustomTooltip from 'components/CustomTooltip';
import { useTranslation } from 'services/localization';
import { setOnboardingActionDone } from 'services/onboarding/utils';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface Props {
	open: boolean;
	onCancel: () => void;
	onCreate: (skuId: string) => void;
	skuItems: SkuItem[];
	shopId: string;
}

interface NewSkuItemParams {
	id: string;
	name: string;
	skuCode: string;
	itemType: SkuItem['itemType'];
}

const getNewEmptySkuItem = (): NewSkuItemParams => ({
	id: newFirestoreId(),
	name: '',
	skuCode: '',
	itemType: 'single',
});

const AddSku = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [newSkuItem, setNewSkuItem] = useState<NewSkuItemParams>(getNewEmptySkuItem());
	const [saving, setSaving] = useState<boolean>(false);
	const skuCodes = uniq(props.skuItems.map((v) => v.skuCode));
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const handleSkuNameChange = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const emptySkuCode = newSkuItem.skuCode === '';
		const skuName = e.target.value;
		const skuCode =
			!!skuName && emptySkuCode ? nameToSkuCode(skuName, skuCodes) : newSkuItem.skuCode;
		setNewSkuItem({ ...newSkuItem, name: skuName, skuCode });
	};

	const handleSkuCodeChange = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setNewSkuItem({ ...newSkuItem, skuCode: e.target.value });
	};

	const handleSkuTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewSkuItem({ ...newSkuItem, itemType: e.target.value as SkuItem['itemType'] });
	};

	const saveSku = async () => {
		if (!validSkuItem) return;
		setSaving(true);
		const newSku = getNewSkuItem({
			id: newSkuItem.id,
			name: newSkuItem.name,
			skuCode: newSkuItem.skuCode,
			shopId: props.shopId,
			itemType: newSkuItem.itemType,
		});
		await api().skuItems.doc(newSku.id).set(newSku);
		setSaving(false);
		setNewSkuItem(getNewEmptySkuItem());
		setOnboardingActionDone('SKU_CREATION', shopAnalytics);
		props.onCreate(newSku.id);
	};

	const validSkuItem = !!newSkuItem.name && !!newSkuItem.skuCode;

	return (
		<Dialog open={props.open} onClose={props.onCancel}>
			<DialogTitle>{t('inventory.addSkuDialog.title', 'New Stock Keeping Unit')}</DialogTitle>
			<DialogContent>
				<Box display="flex" alignItems="flex-start" justifyContent="flex-start">
					<Box flex="0 1 auto">
						<Typography variant="body1">
							{t(
								'inventory.addSkuDialog.description',
								'Add new Stock Keeping Unit (SKU) to your inventory.',
							)}
						</Typography>
					</Box>
					<Box ml={1} mt="-2px">
						<CustomTooltip
							placement="bottom-end"
							minWidth={450}
							title={
								<ul className={classes.tooltipList}>
									<li>
										<Typography variant="caption" className={classes.tooltipListText}>
											{t(
												'inventory.addSkuDialog.descriptionTooltipBody1',
												'SKUs are used to track and group your inventory assets',
											)}
										</Typography>
									</li>
									<li>
										<Typography variant="caption" className={classes.tooltipListText}>
											{t(
												'inventory.addSkuDialog.descriptionTooltipBody2',
												'Use an SKU to define a distinct type of asset in your inventory',
											)}
										</Typography>
									</li>
									<li>
										<Typography variant="caption" className={classes.tooltipListText}>
											{t(
												'inventory.addSkuDialog.descriptionTooltipBody3',
												'Each article in your inventory will belong to one SKU',
											)}
										</Typography>
									</li>
								</ul>
							}
						>
							<IconButton color="secondary" size="small" aria-label="info">
								<RiInformationLine size={20} />
							</IconButton>
						</CustomTooltip>
					</Box>
				</Box>
				<Box mt={4} />
				<FormLabel>{t('inventory.addSkuDialog.detailsForm', 'SKU details')}</FormLabel>
				<Box mt={1}>
					<Grid container spacing={2}>
						<Grid item>
							<TextField
								label={t('inventory.addSkuDialog.detailsFormSkuName', 'SKU name')}
								placeholder={t(
									'inventory.addSkuDialog.detailsFormSkuNamePlaceholder',
									'E.g. Specialized Epic Evo',
								)}
								variant="outlined"
								defaultValue={newSkuItem?.name}
								onBlur={handleSkuNameChange}
							/>
						</Grid>
						<Grid item>
							<TextField
								label={t('inventory.addSkuDialog.detailsFormSkuCode', 'SKU code')}
								placeholder={t(
									'inventory.addSkuDialog.detailsFormSkuCodePlaceholder',
									'E.g. BIK01',
								)}
								variant="outlined"
								value={newSkuItem?.skuCode}
								onChange={handleSkuCodeChange}
							/>
						</Grid>
					</Grid>
				</Box>
				<Box mt={4} />
				<Box display="flex" alignItems="center" justifyContent="flex-start">
					<Box flex="0 1 auto">
						<FormLabel>
							{t('inventory.addSkuDialog.trackingForm', 'How is this SKU tracked?')}
						</FormLabel>
					</Box>
					<Box ml={0.5} mt="-2px">
						<CustomTooltip
							placement="bottom"
							minWidth={400}
							title={
								<ul className={classes.tooltipList}>
									<li>
										<Typography variant="caption" className={classes.tooltipListText}>
											{t(
												'inventory.addSkuDialog.trackingFormTooltipIndividual',
												'Individual - Track each SKU article individually',
											)}
										</Typography>
									</li>
									<li>
										<Typography variant="caption" className={classes.tooltipListText}>
											{t(
												'inventory.addSkuDialog.trackingFormTooltipBulk',
												'Bulk - Track only the total quantity of SKU articles',
											)}
										</Typography>
									</li>
								</ul>
							}
						>
							<IconButton color="secondary" size="small" aria-label="info">
								<RiInformationLine size={20} />
							</IconButton>
						</CustomTooltip>
					</Box>
				</Box>
				<RadioGroup
					aria-label="Stock Type"
					name="stockType"
					value={newSkuItem.itemType}
					onChange={handleSkuTypeChange}
				>
					<FormControlLabel value="single" control={<Radio color="primary" />} label="Individual" />
					<FormControlLabel value="bulk" control={<Radio color="primary" />} label="Bulk" />
				</RadioGroup>
			</DialogContent>
			<Box my={2}>
				<DialogActions>
					<Button onClick={props.onCancel} color="secondary" disabled={saving}>
						{t('common:actions.cancel')}
					</Button>
					<Button
						onClick={saveSku}
						color="primary"
						variant="contained"
						disabled={saving || !validSkuItem}
					>
						{saving ? <Spinner padding={8} /> : t('common:actions.save')}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tooltipList: {
			padding: theme.spacing(0, 2, 0, 3),
		},
		tooltipListText: {
			verticalAlign: 'middle',
		},
	}),
);

export default AddSku;
